import { useState } from 'react';
import { useCustomerUnauthorizedMessages } from '../../../hooks/useCustomerUnauthorizedMessages';
import useHandleApiError from '../../../hooks/useHandleApiError';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { useTranslation } from '../../translation/TranslationContext';
import { OnConfirmData } from '../copy-order/types/copyOrderModal.types';
import {
  BatchCopyOrdersParams,
  BatchUpdateOrdersParams,
} from '../interfaces/orderBatchRepository.interfaces';
import { IUseCopyOrder } from '../interfaces/orderCopy.interface';
import ordersBatchRepository from '../repositories/ordersBatch.repository';

const { handleMessage } = snackbarStore;

interface UseCopyOrdersBatchParams extends IUseCopyOrder {
  batchUpdateOrdersParams: BatchUpdateOrdersParams;
}

export const useCopyOrdersBatch = ({
  batchUpdateOrdersParams,
  onClose,
  reload,
}: UseCopyOrdersBatchParams) => {
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();
  const { showUnauthorizedToCopyOrderProducts, showUnauthorizedOrderProducts } =
    useCustomerUnauthorizedMessages();
  const repository = ordersBatchRepository();

  const [invalidCustomerIds, setInvalidCustomerIds] = useState<string[]>([]);

  const handleCopyErrorWithInvalidCustomers = (
    selectedCustomersCount: number,
    invalidIds?: string[],
  ) => {
    if (!invalidIds || invalidIds.length === 0) {
      return;
    }

    setInvalidCustomerIds(invalidIds);

    if (selectedCustomersCount > 1) {
      showUnauthorizedToCopyOrderProducts();
    } else {
      showUnauthorizedOrderProducts();
    }
  };

  const handleBatchCopyInfo = () => {
    handleMessage({
      type: 'info',
      message: translate(LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_FEEDBACK),
    });
    setInvalidCustomerIds([]);
    if (reload) {
      reload();
    }
    onClose();
  };

  const handleBatchCopyError = (
    response: Awaited<ReturnType<typeof repository.batchCopyOrders>>,
    data: OnConfirmData,
  ) => {
    if (response.ok) {
      return;
    }

    const code = response.data?.code;
    if (code === LangEnum.API_EXCEPTION_CUSTOMER_UNAUTHORIZED_TO_COPY_ORDER_PRODUCTS) {
      const invalidIds = response.data?.data?.invalidCustomerIds;
      handleCopyErrorWithInvalidCustomers(data.customerGroups?.length || 0, invalidIds);
    }

    const reason = getApiErrorMessage(response, translate(LangEnum.ERROR_UPDATING_ITEM_MESSAGE));

    handleMessage({
      type: 'error',
      message: translate(LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_ERROR_FEEDBACK, { reason }),
    });
  };

  const onCopyOrders = async (data: OnConfirmData): Promise<boolean> => {
    const payload: BatchCopyOrdersParams = {
      ...batchUpdateOrdersParams,
      gridMultiplier: data.gridMultiplier,
      copyOrderWithConditions: data.copyOrderWithConditions,
      asSuggested: !!data.asSuggested,
      customerIds: data.customerGroups?.length
        ? data.customerGroups.map((item: { id: string }) => item.id)
        : undefined,
      keepOriginalCustomer: data.keepOriginalCustomer,
    };

    const response = await repository.batchCopyOrders(payload);

    if (response.ok) {
      handleBatchCopyInfo();
      return true;
    }

    handleBatchCopyError(response, data);
    return false;
  };

  return {
    onCopyOrders,
    invalidCustomerIds,
  };
};
